import "./_add-distribution-button-dropdown.scss";
import {AddDistributionButtonDropdown} from "./AddDistributionButtonDropdown";
import {
    Charity,
    Distribution,
    DistributionType,
    DropdownItemType,
    NonTrustDistribution,
    Outright,
    Trust
} from "../../model/EstateFlowchartTypes";
import React, {useEffect, useState} from "react";
import CopyButtonDropdown from "./CopyButtonDropdown";
import {clearPowersOfAppointmentId, createNonTrustDistribution, createTrust, isTrust} from "../../utils/DefUtils";
import FormPageSection from "../FormPageSection/FormPageSection";
import {scrollToFormSection} from "../../utils/FormContentUtils";
import {navItemSelector} from "../../slices/NavigationSlice";
import {getNavItemPage} from "../FormNav/FormNavUtils";
import {useAppSelector} from "../../../store/hooks";
import {selectReleaseToggles} from "../../../ReleaseToggles/releaseTogglesSlice";

type CopyDistributionSectionProps = {
    distributionTypeDropdownOptions: DropdownItemType[];
    distributions: (Trust | NonTrustDistribution)[]
    setDistributions: (distributions: Distribution[]) => void
};

const AddOrCopyDistributionSection: React.FC<CopyDistributionSectionProps> = (
    {
        distributionTypeDropdownOptions,
        distributions,
        setDistributions
    }: CopyDistributionSectionProps): JSX.Element => {

    const [hasDistributions, setHasDistributions] = useState<boolean>(false);
    const [distributionList, setDistributionList] = useState<Distribution[]>(distributions);
    const activeNavItem = useAppSelector(navItemSelector)
    const releaseToggles = useAppSelector(selectReleaseToggles);

    useEffect(() => {
        setDistributionList(distributions);
    }, [distributions]);

    useEffect(() => {
        setHasDistributions(distributionList.length > 0);
    }, [distributionList]);

    const isThereAnyDistributionsWithValidNames = (distributions: Distribution[]) => {
        if (!distributions) return false;
        return distributions.some(distribution => {
            if (distribution.distributionType !== DistributionType.OUTRIGHT) {
                return distribution.name;
            } else {
                const outright = distribution as Outright;
                return outright.outrightMembers && outright.outrightMembers.length > 0;
            }
        });
    }

    function handleAddDistribution(distributionType: DistributionType) {
        const newDistribution = isTrust(distributionType) ?
            createTrust(distributionType) :
            createNonTrustDistribution(distributionType);

        const newDistributionsList = [...distributionList, newDistribution];
        setDistributionList(newDistributionsList);
        setDistributions(newDistributionsList);

        const page = getNavItemPage(activeNavItem);
        const pageIndex = page?.index;
        const linkIndex = newDistributionsList.length - 1;
        const linkKey = `page${pageIndex}-section${linkIndex}`

        setTimeout(() => {
            scrollToFormSection(linkKey);
        }, 500)
    }

    function cloneCharityDistribution(distribution: Charity, selectedDistributionName: string, releaseToggles: any): Charity {
        const updatedCharity = {
            ...distribution,
            charityBeneficiaries: distribution.charityBeneficiaries.map(beneficiary => ({
                ...beneficiary,
                id: undefined,
                charityId: "",
            })),
        };
        if (releaseToggles?.enableEstateFlowchartMVPRelease) {
            updatedCharity.name = selectedDistributionName;
        }
        return updatedCharity;
    }

    function scrollToNewDistribution(page: any, newDistributionList: Distribution[]) {
        const pageIndex = page?.index;
        const linkIndex = newDistributionList.length - 1;
        const linkKey = `page${pageIndex}-section${linkIndex}`;
        setTimeout(() => {
            scrollToFormSection(linkKey);
        }, 500);
    }

    function handleCopyDistribution(selectedDistributionName: string) {
        if (distributionList.length === 0) return;

        const selectedDistribution = distributionList.find(
            distribution => distribution.name === selectedDistributionName);

        if (!selectedDistribution) return;

        let newDistribution: Distribution = {
            ...selectedDistribution,
            distributionId: undefined,
            name: "",
        };

        if (isTrust(newDistribution.distributionType)) {
            (newDistribution as Trust).powersOfAppointment = clearPowersOfAppointmentId((newDistribution as Trust).powersOfAppointment);
        } else if (newDistribution.distributionType === DistributionType.OUTRIGHT) {
            (newDistribution as Outright).outrightMembers = [];
        } else if (newDistribution.distributionType === DistributionType.CHARITY) {
            newDistribution = cloneCharityDistribution(newDistribution as Charity, selectedDistributionName, releaseToggles);
        }

        const newDistributionList = [...distributionList, newDistribution];
        setDistributionList(newDistributionList);
        setDistributions(newDistributionList);

        const page = getNavItemPage(activeNavItem);
        scrollToNewDistribution(page, newDistributionList);
    }

    return (
        <FormPageSection id="add-copy-distribution-section">
            <div className="add-or-copy-distribution-container">
                {hasDistributions &&
                    <h2 className={"add-copy-trust-heading"}>
                        Continue to add or copy distributions.
                    </h2>
                }
                <div className="add-distribution-dropdown-container">
                    <AddDistributionButtonDropdown
                        className={"add-distribution-button"}
                        items={distributionTypeDropdownOptions}
                        shouldDropdownOpenUp={hasDistributions}
                        handleAddDistribution={handleAddDistribution}
                    />
                    {hasDistributions &&
                        <CopyButtonDropdown
                            className={"copy-distribution-button"}
                            disabled={!isThereAnyDistributionsWithValidNames(distributionList)}
                            shouldDropdownOpenUp={hasDistributions}
                            distributions={distributionList}
                            handleCopyDistribution={handleCopyDistribution}
                        />
                    }
                </div>
            </div>
        </FormPageSection>
    );
};
export default AddOrCopyDistributionSection;